import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { playClick } from "../lib/sounds";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Box } from "./Box";
import { Button } from "./Button";
import { CopyOpenIcon } from "./CopyOpenIcon";
import { Text } from "./Text";
import type { StepData } from "./Workflow";

const Header = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  height: 100%;
`;

const Pill = styled("button")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 38px;
  background: var(--alpha4);
  border: none;
  cursor: pointer;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  color: var(--text-primary);

  &:hover {
    background: var(--alpha8);
  }
`;

type Props = {
  step: StepData;
  email?: string | null;
  right?: React.ReactNode;
  onBack?: () => void;
};

export function StepHeader({ step, email, right, onBack }: Props) {
  return (
    <Header>
      {onBack && (
        <Button
          variant="secondary"
          size="l"
          icon="arrow-left"
          type="button"
          aria-label="Back"
          onMouseDown={onBack}
        />
      )}
      <div
        style={{
          flex: 1,
          width: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Box vertical={8}>
          <Text variant="text-regular-md" ellipsis>
            {step.name}
          </Text>
        </Box>
        {email && (
          <CopyPill
            displayContent={email}
            copyText={email}
            openUrl={`mailto:${email}`}
            title={email}
          />
        )}
      </div>
      {right}
    </Header>
  );
}

type CopyPillProps = {
  displayContent: React.ReactNode;
  copyText: string;
  openUrl: string;
  title: string;
};

function CopyPill({ displayContent, copyText, openUrl, title }: CopyPillProps) {
  const [copied, setCopied] = useState(false);
  const [cmdHeld, setCmdHeld] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => setCmdHeld(e.metaKey);
    const onBlur = () => setCmdHeld(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    playClick();
    if (e.metaKey) {
      window.open(openUrl, "_blank");
      return;
    }
    void writeToClipboard(copyText);
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
  };

  const iconState = copied ? "copied" : cmdHeld ? "cmd" : undefined;

  return (
    <Pill
      type="button"
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        handleClick(e);
      }}
      title={title}
    >
      <Text variant="text-small-md" color="var(--text-primary)" ellipsis>
        {displayContent}
      </Text>
      <CopyOpenIcon state={iconState} size={10} />
    </Pill>
  );
}
